import React, { PureComponent, Component } from "react";
import styled from "styled-components/macro";

import {
  zoomModeInfo,
  VERTICAL_SEPARATOR_COLOR,
  SELECT_GREEN,
  ZOOM_MODE_YEAR,
  HEADER_UNIT_SUB_HOUR,
  HEADER_UNIT_SUB_MONTH,
  SIDELIST_ROW_BORDER,
} from "../../helpers/Const";
import { getChartTotalWidth, hexToRGB } from "../../helpers/StyleHelpers";
import { differenceInHours, isWeekend, addDays, startOfToday, getDaysInMonth, startOfMonth } from "date-fns";
import { getNextRoundedDate, isHoliday } from "../../helpers/DateHelpers";
import { connect } from "react-redux";
import { iRootState, Dispatch } from "src/rematch/store";
import { DisplaySettings } from "src/rematch/interfaces/states/IViewSettingsState";
import { Action } from "@rematch/core";

interface IViewPortBackgroundRootProps {
  width: number;
  height: number;
}

const ViewPortBackgroundRoot = styled.div`
  width: ${(props: IViewPortBackgroundRootProps) => props.width}px;
  height: ${(props: IViewPortBackgroundRootProps) => props.height}px;
  position: absolute;
  overflow: hidden;
`;

interface IViewPortBackgroundCurrentDayProps {
  width: number;
  left: number;
}

const ViewPortBackgroundCurrentDay = styled.div`
  width: ${(props: IViewPortBackgroundCurrentDayProps) => props.width}px;
  left: ${(props: IViewPortBackgroundCurrentDayProps) => props.left}px;
  background: ${hexToRGB(SELECT_GREEN, 0.4)};
  height: 100%;
  position: absolute;
`;

const HorizontalBarsRoot = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
`;

interface IHorizontalBarProps {
  height: number;
}

const HorizontalBar = styled.div`
  height: ${(props: IHorizontalBarProps) => props.height}px;
  background: #fff;
  border-bottom: 1px solid ${SIDELIST_ROW_BORDER};
`;

const VerticalBarsRoot = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  z-index: 1;
  pointer-events: none;
`;

const VerticalBarsMajorUnitRoot = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
`;

interface IVerticalBarsMajorUnitItemProps {
  width: number;
}

const VerticalBarsMajorUnitItem = styled.div`
  width: ${(props: IVerticalBarsMajorUnitItemProps) => props.width}px;
  height: 100%;
  border-right: 1px solid ${VERTICAL_SEPARATOR_COLOR};
  float: left;
`;

const VerticalBarsWeekendHighlightRoot = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
`;

const VerticalBarsWeekendHighlightItem = styled.div`
  height: 100%;
  background: rgba(0, 0, 0, 0.05);
  position: absolute;
`;

interface IViewPortBackgroundHorizontalBarsProps {
  taskCount: number;
  taskHeight: number;
}

class ViewPortBackgroundHorizontalBars extends PureComponent<IViewPortBackgroundHorizontalBarsProps> {
  render() {
    const horizontalBars = [];
    for (let i = 0; i < this.props.taskCount; i++) {
      horizontalBars.push(<HorizontalBar key={i} height={this.props.taskHeight}></HorizontalBar>);
    }
    return <HorizontalBarsRoot>{horizontalBars}</HorizontalBarsRoot>;
  }
}

interface IViewPortBackgroundVerticalBarsProps {
  displaySettings: DisplaySettings;
  zoomMode: string;
  startDate: Date;
  endDate: Date;
  hourWidth: number;
}

class ViewPortBackgroundVerticalBarsBase extends PureComponent<IViewPortBackgroundVerticalBarsProps> {
  renderMajorUnitLines() {
    const elements = [];
    const majorHeaderUnit = zoomModeInfo[this.props.zoomMode].headerUnits[0];
    let currentDate = this.props.startDate;
    let i = 0;
    let nextDate = getNextRoundedDate(majorHeaderUnit, currentDate, this.props.displaySettings.startOfWeekDay);
    while (nextDate < this.props.endDate) {
      const dateDiffInPixels = differenceInHours(nextDate, currentDate) * this.props.hourWidth;
      elements.push(
        <VerticalBarsMajorUnitItem key={i} width={i === 0 ? dateDiffInPixels + 1 : dateDiffInPixels}></VerticalBarsMajorUnitItem>
      );
      currentDate = nextDate;
      nextDate = getNextRoundedDate(majorHeaderUnit, currentDate, this.props.displaySettings.startOfWeekDay);
      i++;
    }
    return elements;
  }
  renderWeekendHighlights() {
    if (this.props.zoomMode === ZOOM_MODE_YEAR) {
      return;
    }
    const elements = [];
    let i = 0;
    let nextDate = addDays(this.props.startDate, 1);
    while (nextDate < this.props.endDate) {
      const dayIsHoliday = this.props.displaySettings.showHolidays ? isHoliday(nextDate) : false;
      if (isWeekend(nextDate) || dayIsHoliday) {
        const dateDiffInPixels = differenceInHours(nextDate, this.props.startDate) * this.props.hourWidth;
        elements.push(
          <VerticalBarsWeekendHighlightItem
            key={i}
            style={{
              left: dateDiffInPixels,
              width: this.props.hourWidth * 24,
            }}
          ></VerticalBarsWeekendHighlightItem>
        );
      }
      nextDate = addDays(nextDate, 1);
      i++;
    }
    return elements;
  }

  render() {
    const zoomSubUnit = zoomModeInfo[this.props.zoomMode].headerUnits[1];
    const now = new Date();
    let currentTimeWidth;
    let currentTimeLeft;
    if (zoomSubUnit === HEADER_UNIT_SUB_HOUR) {
      currentTimeWidth = this.props.hourWidth;
      currentTimeLeft = this.props.hourWidth * differenceInHours(now, this.props.startDate);
    } else if (zoomSubUnit === HEADER_UNIT_SUB_MONTH) {
      currentTimeWidth = this.props.hourWidth * getDaysInMonth(now) * 24;
      currentTimeLeft = this.props.hourWidth * differenceInHours(startOfMonth(now), this.props.startDate);
    } else {
      //minor unit is one day
      currentTimeWidth = this.props.hourWidth * 24;
      currentTimeLeft = this.props.hourWidth * differenceInHours(startOfToday(), this.props.startDate);
    }

    return (
      <VerticalBarsRoot>
        <VerticalBarsMajorUnitRoot>{this.renderMajorUnitLines()}</VerticalBarsMajorUnitRoot>
        <VerticalBarsWeekendHighlightRoot>{this.renderWeekendHighlights()}</VerticalBarsWeekendHighlightRoot>
        {this.props.displaySettings.showCurrentDay ? (
          <ViewPortBackgroundCurrentDay width={currentTimeWidth} left={currentTimeLeft} />
        ) : null}
      </VerticalBarsRoot>
    );
  }
}

const mapState = (state: iRootState) => ({
  displaySettings: state.UI.displaySettings,
});

const ViewPortBackgroundVerticalBars = connect(mapState, null)(ViewPortBackgroundVerticalBarsBase);

interface IViewportBackgroundProps {
  taskHeight: number;
  taskCount: number;
  hourWidth: number;
  zoomMode: string;
  startDate: Date;
  endDate: Date;
  dragUnderway: boolean;
  onSelectItem: (taskId: number) => Action<number,void>;
}

class ViewportBackground extends Component<IViewportBackgroundProps> {
  shouldComponentUpdate(prevProps: IViewportBackgroundProps) {
    for (const propKey in prevProps) {
      if (propKey === "dragUnderway") {
        continue;
      } else if (prevProps[propKey] !== this.props[propKey]) {
        return true;
      }
    }
    return false;
  }

  deselectItem = () => {
    if (!this.props.dragUnderway) {
      this.props.onSelectItem(null);
    }
  };

  render() {
    const chartTotalWidth = getChartTotalWidth(this.props.hourWidth, this.props.startDate, this.props.endDate);

    return (
      <ViewPortBackgroundRoot onClick={this.deselectItem} width={chartTotalWidth} height={this.props.taskCount * this.props.taskHeight}>
        <ViewPortBackgroundHorizontalBars taskCount={this.props.taskCount} taskHeight={this.props.taskHeight} />
        <ViewPortBackgroundVerticalBars
          hourWidth={this.props.hourWidth}
          zoomMode={this.props.zoomMode}
          startDate={this.props.startDate}
          endDate={this.props.endDate}
        />
      </ViewPortBackgroundRoot>
    );
  }
}
const mapDispatch = (dispatch: Dispatch) => ({
  onSelectItem: (taskId: number) => dispatch.items.selectItem(taskId),
});

const mapStateBg = (state: iRootState) => ({
  taskHeight: state.UI.displaySettings.taskHeight,
});

export default connect(mapStateBg, mapDispatch)(ViewportBackground);
